import { useAtom } from "jotai";
import React, { useState } from "react";
import { currentSpaceAtom, currentUserAtom } from "../jotai/atoms";
import { useAuth } from "../auth/useAuth";
import Dialog, { Actions, Close } from "../components/Dialog";
import { Form, Input, Submit } from "../components/Form";
import { useSpaceInfo } from "../roomservice/maps";
import { styled } from "../stitches.config";
import { ButtonForwardRef } from "../components/Button";
import { Space } from "../types";
import { Helmet } from "react-helmet-async";

// Screen-reader-friendly title
export default function SpaceName() {
  const [currentSpace] = useAtom(currentSpaceAtom);
  const [info] = useSpaceInfo();
  const title =
    "Makespace" +
    (currentSpace && ` — ${info?.name || currentSpace.id.slice(0, 6)}`);
  return (
    <>
      <h1 className="visually-hidden">{title}</h1>
      <Helmet>
        <title>{title}</title>
      </Helmet>
      <SpaceNameContents currentSpace={currentSpace} />
    </>
  );
}

interface Props {
  currentSpace: Space | undefined;
}

function SpaceNameContents({ currentSpace }: Props) {
  const [currentUser] = useAtom(currentUserAtom);
  const [name, setName] = useSpaceName();
  const { auth } = useAuth();
  const [open, setOpen] = useState(false);

  if (!currentUser || !currentSpace) return <></>;

  let displayName = name || currentSpace.id.slice(0, 6);

  if (!auth) return <NameSpan>{displayName}</NameSpan>;

  function changeName(newName: string) {
    setName(newName);
    setOpen(false);
  }

  return (
    <Dialog
      open={open}
      setOpen={setOpen}
      label={displayName}
      title="Space name"
      ariaLabel="Edit space name"
      triggerAs={NameButton}
    >
      <NameForm currentName={displayName} changeName={changeName} />
    </Dialog>
  );
}

interface NameFormProps {
  currentName: string;
  changeName: (name: string) => void;
}

function NameForm({ currentName, changeName }: NameFormProps) {
  const [name, setName] = useState(currentName);

  function onChange(e: React.ChangeEvent<HTMLInputElement>) {
    setName(e.target.value);
  }

  function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    changeName(name);
  }

  return (
    <Form onSubmit={onSubmit}>
      <Input
        name="name"
        type="text"
        placeholder="Enter name..."
        spellCheck={false}
        required
        onChange={onChange}
        value={name}
      />
      <Actions>
        <Submit>Submit</Submit>
        <Close secondary>Cancel</Close>
      </Actions>
    </Form>
  );
}

function useSpaceName() {
  const [info, infoClient] = useSpaceInfo();
  const name = info?.name;

  function setName(newName: string) {
    infoClient?.set("name", newName);
  }
  return [name, setName] as const;
}

const nameStyles = {
  fontWeight: "$medium",
  color: "$brown",
};

const NameSpan = styled("span", { ...nameStyles });
const NameButton = styled(ButtonForwardRef, { ...nameStyles });
